import { c as _c } from "react/compiler-runtime";
import { Avatar, Box, Chip, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import React from "react";
import { categoryLabels } from "#/entities/category/model/categoryLabels";
import { getDifficultyColor } from "#/entities/difficulty/lib/getDifficultyColor";
import { difficultyLabels } from "#/entities/difficulty/model/difficultyLabels";
import type { RouterOutputs } from "#/shared/api";
import { getImageUrl } from "#/shared/lib";
import { NewLabel } from "#/shared/ui/atoms/NewLabel";

// Support both allBrief and browseProjects types (they have compatible structures)
type ProjectBrief = RouterOutputs["project"]["allBrief"][number] | RouterOutputs["project"]["browseProjects"]["projects"][number];
type ProjectBrowserItemProps = {
  project: ProjectBrief;
  isSelected: boolean;
  onClick: () => void;
};
const ProjectBrowserItemComponent: React.FC<ProjectBrowserItemProps> = t0 => {
  const $ = _c(54);
  const {
    project,
    isSelected,
    onClick
  } = t0;
  const theme = useTheme();
  let t1;
  if ($[0] !== onClick) {
    t1 = ev => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        onClick();
      }
    };
    $[0] = onClick;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleKeyDown = t1;
  let t2;
  if ($[2] !== project.difficulty || $[3] !== theme) {
    t2 = project.difficulty ? getDifficultyColor(theme, project.difficulty) : undefined;
    $[2] = project.difficulty;
    $[3] = theme;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const difficultyColor = t2;
  const t3 = isSelected ? theme.palette.action.selected : "transparent";
  const t4 = `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`;
  const t5 = isSelected ? theme.palette.primary.main : "transparent";
  let t6;
  if ($[5] !== theme.palette.action.hover) {
    t6 = {
      backgroundColor: theme.palette.action.hover
    };
    $[5] = theme.palette.action.hover;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = `2px solid ${theme.palette.primary.main}`;
  let t8;
  if ($[7] !== t7) {
    t8 = {
      outline: t7,
      outlineOffset: -2
    };
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5 || $[12] !== t6 || $[13] !== t8) {
    t9 = {
      height: 76,
      p: 1.75,
      px: 2,
      cursor: "pointer",
      borderRadius: 1.5,
      backgroundColor: t3,
      border: t4,
      borderColor: t5,
      "&:hover": t6,
      "&:focus-visible": t8,
      transition: "background-color 0.15s ease-out"
    };
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = `Select project ${project.title}`;
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Box sx={{
      width: 20,
      height: 20,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }} aria-label="Completion status" />;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = isSelected ? 600 : 500;
  const t13 = isSelected ? theme.palette.text.primary : theme.palette.text.primary;
  let t14;
  if ($[16] !== t12 || $[17] !== t13) {
    t14 = {
      fontWeight: t12,
      fontSize: "0.95rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: t13
    };
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== project.title || $[20] !== t14) {
    t15 = <Typography variant="body1" sx={t14}>{project.title}</Typography>;
    $[19] = project.title;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== project.createdAt || $[23] !== project.isNew) {
    t16 = project.isNew && <NewLabel createdAt={project.createdAt} />;
    $[22] = project.createdAt;
    $[23] = project.isNew;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t15 || $[26] !== t16) {
    t17 = <Stack direction="row" spacing={1} alignItems="center" flexWrap="nowrap">{t15}{t16}</Stack>;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      color: "text.secondary",
      fontSize: "0.75rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    };
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  const t19 = categoryLabels[project.category];
  let t20;
  if ($[29] !== t19) {
    t20 = <Stack direction="row" spacing={1} alignItems="center"><Typography variant="caption" sx={t18}>{t19}</Typography></Stack>;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== t17 || $[32] !== t20) {
    t21 = <Stack direction="row" spacing={1.5} alignItems="center" flex={1} minWidth={0}>{t11}<Stack direction="column" spacing={0.5} flex={1} minWidth={0}>{t17}{t20}</Stack></Stack>;
    $[31] = t17;
    $[32] = t20;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      minWidth: 0,
      flexShrink: 0
    };
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== difficultyColor || $[36] !== project.difficulty || $[37] !== theme.palette.divider || $[38] !== theme.palette.text.secondary) {
    t23 = project.difficulty && <Chip label={difficultyLabels[project.difficulty]} size="small" sx={{
      height: 24,
      fontSize: "0.7rem",
      fontWeight: 600,
      backgroundColor: difficultyColor ? `${difficultyColor}20` : "transparent",
      color: difficultyColor || theme.palette.text.secondary,
      border: difficultyColor ? `1px solid ${difficultyColor}40` : `1px solid ${theme.palette.divider}`,
      "& .MuiChip-label": {
        px: 1
      }
    }} />;
    $[35] = difficultyColor;
    $[36] = project.difficulty;
    $[37] = theme.palette.divider;
    $[38] = theme.palette.text.secondary;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== project.author || $[41] !== theme.palette.divider) {
    t24 = project.author?.bucketImage && <Tooltip title={`Author: ${project.author.name}`} arrow={true}><Avatar src={getImageUrl(project.author.bucketImage)} alt={`${project.author.name} avatar`} sx={{
        height: 28,
        width: 28,
        border: `2px solid ${theme.palette.divider}`
      }} /></Tooltip>;
    $[40] = project.author;
    $[41] = theme.palette.divider;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== t23 || $[44] !== t24) {
    t25 = <Stack direction="row" alignItems="center" spacing={1.5} sx={t22}>{t23}{t24}</Stack>;
    $[43] = t23;
    $[44] = t24;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== handleKeyDown || $[47] !== isSelected || $[48] !== onClick || $[49] !== t10 || $[50] !== t21 || $[51] !== t25 || $[52] !== t9) {
    t26 = <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5} onClick={onClick} onKeyDown={handleKeyDown} tabIndex={0} sx={t9} role="button" aria-selected={isSelected} aria-label={t10}>{t21}{t25}</Stack>;
    $[46] = handleKeyDown;
    $[47] = isSelected;
    $[48] = onClick;
    $[49] = t10;
    $[50] = t21;
    $[51] = t25;
    $[52] = t9;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  return t26;
};
ProjectBrowserItemComponent.displayName = "ProjectBrowserItem";
export const ProjectBrowserItem = React.memo(ProjectBrowserItemComponent, (prevProps, nextProps) => {
  // Custom comparison: return true if props are equal (skip re-render)
  // Only re-render if project id, isSelected, or onClick reference changes
  return prevProps.project.id === nextProps.project.id && prevProps.isSelected === nextProps.isSelected && prevProps.onClick === nextProps.onClick && prevProps.project.slug === nextProps.project.slug && prevProps.project.title === nextProps.project.title && prevProps.project.category === nextProps.project.category && prevProps.project.difficulty === nextProps.project.difficulty;
});